import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { verifyMagicLink } from '../api/auth';
import { useAuth } from '../auth/useAuth';
import { nationalityToLanguage } from '../i18n/nationalityMapping';
import { SUPPORTED_LANGUAGES } from '../i18n/languageConfig';
import type { SupportedLanguage } from '../i18n/languageConfig';
import LanguageSwitcher from '../components/LanguageSwitcher';

const STORAGE_KEY = 'worker-app-language';

const AuthVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const verifyAttempted = useRef(false);

  useEffect(() => {
    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    const token = searchParams.get('token');
    const redirect = searchParams.get('redirect');
    if (!token) {
      setError(t('auth.invalidLink'));
      return;
    }

    verifyMagicLink(token)
      .then((result) => {
        // Auto-detect language if user hasn't manually chosen one
        if (!localStorage.getItem(STORAGE_KEY)) {
          let detected: SupportedLanguage | null = null;

          // Priority 1: worker's preferred language from backend
          if (result.user.preferredLanguage) {
            const pref = result.user.preferredLanguage as SupportedLanguage;
            if ((SUPPORTED_LANGUAGES as readonly string[]).includes(pref)) {
              detected = pref;
            }
          }

          // Priority 2: map nationality to language
          if (!detected && result.user.nationality) {
            detected = nationalityToLanguage(result.user.nationality);
          }

          if (detected) {
            i18n.changeLanguage(detected);
          }
        }

        login(result.accessToken, result.refreshToken, result.user);
        navigate(redirect || result.redirectTo || '/dashboard', { replace: true });
      })
      .catch(() => {
        setError(t('auth.expiredLink'));
      });
  }, [searchParams, navigate, login, t, i18n]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <LanguageSwitcher />
        </Box>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('auth.linkExpiredTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary">
        {t('auth.loggingIn')}
      </Typography>
    </Box>
  );
};

export default AuthVerifyPage;
